import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getChannelProfile } from '../../services/api';
import classes from './ChannelProfile.module.css';

interface ChannelVideoInfo {
    videoId: string;
    title: string;
    viewsCount: number;
    likesCount: number;
    commentsCount: number;
    publishedAt: string;
    thumbnailUrl: string;
}

interface ChannelProfileData {
    channelId: string;
    channelName: string;
    channelAvatar: string;
    subscribersCount: number;
    totalChannelViews: number;
    totalVideosCount: number;
    category: string;
    region: string;
    urlToChannel: string;
    totalLikes: number;
    totalComments: number;
    averageViewsPerVideo: number;
    mostViewedVideoTitle: string;
    mostViewedVideoViews: number;
    mostViewedVideoId: string;
    topVideos: ChannelVideoInfo[];
}

function decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

function formatNumber(num: number): string {
    if (num >= 1_000_000_000) {
        return (num / 1_000_000_000).toFixed(1) + 'B';
    }
    if (num >= 1_000_000) {
        return (num / 1_000_000).toFixed(1) + 'M';
    }
    if (num >= 1_000) {
        return (num / 1_000).toFixed(1) + 'K';
    }
    return num.toLocaleString('it-IT');
}

function formatFullNumber(num: number): string {
    return num.toLocaleString('it-IT');
}

export function ChannelProfile() {
    const { channelId } = useParams<{ channelId: string }>();
    const [profile, setProfile] = useState<ChannelProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function fetchProfile() {
            if (!channelId) return;
            try {
                setLoading(true);
                const response = await getChannelProfile(channelId);
                setProfile(response.data);
                setError(null);
            } catch (err: any) {
                if (err.response?.status === 404) {
                    setError('Canale non trovato');
                } else {
                    setError('Impossibile caricare il profilo del canale');
                }
            } finally {
                setLoading(false);
            }
        }
        fetchProfile();
    }, [channelId]);

    if (loading) {
        return <div className={classes.loading}>Caricamento...</div>;
    }

    if (error || !profile) {
        return <div className={classes.error}>{error || 'Errore sconosciuto'}</div>;
    }

    const getRankClass = (index: number) => {
        if (index === 0) return classes.videoRankGold;
        if (index === 1) return classes.videoRankSilver;
        if (index === 2) return classes.videoRankBronze;
        return '';
    };

    return (
        <div className={classes.container}>
            {/* Hero Section */}
            <div className={classes.hero}>
                <div className={classes.heroContent}>
                    <div className={classes.avatarWrapper}>
                        <img
                            src={profile.channelAvatar}
                            alt={profile.channelName}
                            className={classes.avatar}
                        />
                    </div>
                    <div className={classes.heroInfo}>
                        <h1 className={classes.channelName}>
                            {decodeHtmlEntities(profile.channelName)}
                        </h1>
                        <div className={classes.channelMeta}>
                            <span className={`${classes.metaBadge} ${classes.subscribersBadge}`}>
                                👥 {formatNumber(profile.subscribersCount)} iscritti
                            </span>
                            {profile.category && (
                                <span className={classes.metaBadge}>
                                    🏷️ {profile.category}
                                </span>
                            )}
                            {profile.region && (
                                <span className={classes.metaBadge}>
                                    🌍 {profile.region}
                                </span>
                            )}
                        </div>
                        {profile.urlToChannel && (
                            <a
                                href={profile.urlToChannel.startsWith('http') ? profile.urlToChannel : `https://www.youtube.com/${profile.urlToChannel}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.ytButton}
                            >
                                ▶ Vai al Canale YouTube
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className={classes.statsGrid}>
                <div className={classes.statCard}>
                    <div className={classes.statIcon}>👁️</div>
                    <div className={classes.statValue}>{formatNumber(profile.totalChannelViews)}</div>
                    <div className={classes.statLabel}>Views Totali</div>
                </div>
                <div className={classes.statCard}>
                    <div className={classes.statIcon}>🎬</div>
                    <div className={classes.statValue}>{formatNumber(profile.totalVideosCount)}</div>
                    <div className={classes.statLabel}>Video</div>
                </div>
                <div className={classes.statCard}>
                    <div className={classes.statIcon}>❤️</div>
                    <div className={classes.statValue}>{formatNumber(profile.totalLikes)}</div>
                    <div className={classes.statLabel}>Like Totali</div>
                </div>
                <div className={classes.statCard}>
                    <div className={classes.statIcon}>💬</div>
                    <div className={classes.statValue}>{formatNumber(profile.totalComments)}</div>
                    <div className={classes.statLabel}>Commenti</div>
                </div>
                <div className={classes.statCard}>
                    <div className={classes.statIcon}>📊</div>
                    <div className={classes.statValue}>{formatNumber(profile.averageViewsPerVideo)}</div>
                    <div className={classes.statLabel}>Media Views</div>
                </div>
            </div>

            {/* Most Viewed Video Highlight */}
            {profile.mostViewedVideoId && (
                <a
                    href={`https://www.youtube.com/watch?v=${profile.mostViewedVideoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <div className={classes.highlight}>
                        <div className={classes.highlightIcon}>🏆</div>
                        <div className={classes.highlightInfo}>
                            <div className={classes.highlightLabel}>Video più visto</div>
                            <h3 className={classes.highlightTitle}>
                                {decodeHtmlEntities(profile.mostViewedVideoTitle)}
                            </h3>
                            <div className={classes.highlightViews}>
                                {formatFullNumber(profile.mostViewedVideoViews)} visualizzazioni
                            </div>
                        </div>
                    </div>
                </a>
            )}

            {/* Top Videos */}
            {profile.topVideos.length > 0 && (
                <>
                    <h2 className={classes.sectionTitle}>
                        🔥 Top Video del Canale
                    </h2>
                    <div className={classes.videosGrid}>
                        {profile.topVideos.map((video, index) => (
                            <a
                                key={video.videoId}
                                href={`https://www.youtube.com/watch?v=${video.videoId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.videoCard}
                            >
                                <div className={`${classes.videoRank} ${getRankClass(index)}`}>
                                    {index + 1}
                                </div>
                                {video.thumbnailUrl && (
                                    <img
                                        src={video.thumbnailUrl}
                                        alt={video.title}
                                        className={classes.videoThumbnail}
                                    />
                                )}
                                <div className={classes.videoInfo}>
                                    <h4 className={classes.videoTitle}>
                                        {decodeHtmlEntities(video.title)}
                                    </h4>
                                    <div className={classes.videoStats}>
                                        <span className={classes.videoStat}>
                                            👁️ <span className={classes.videoStatValue}>{formatNumber(video.viewsCount)}</span>
                                        </span>
                                        <span className={classes.videoStat}>
                                            ❤️ <span className={classes.videoStatValue}>{formatNumber(video.likesCount)}</span>
                                        </span>
                                        <span className={classes.videoStat}>
                                            💬 <span className={classes.videoStatValue}>{formatNumber(video.commentsCount)}</span>
                                        </span>
                                    </div>
                                    <div className={classes.videoDate}>
                                        📅 {video.publishedAt}
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
